import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ExportButton } from '@/components/common/ExportButton';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { DoctrinePanel } from '@/pages/anatel/DoctrinePanel';
import {
  fetchPafDecisionDoctrine,
  fetchPafDecisionFilters,
  fetchPafDecisions,
} from '@/services/anatel';
import type { PafDecisionFilters } from '@/services/types';

const PAGE_SIZE = 50;

export function AnatelPafDecisionsPage() {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const [query, setQuery] = useState('');
  const [relator, setRelator] = useState('');
  const [outcome, setOutcome] = useState('');
  const [year, setYear] = useState('');
  const [citation, setCitation] = useState('');
  const [offset, setOffset] = useState(0);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['anatel', 'paf', 'decisions', query, relator, outcome, year, citation, offset],
    queryFn: () =>
      fetchPafDecisions({
        query: query || null,
        relator: relator || null,
        outcome: outcome || null,
        year: year || null,
        citation: citation || null,
        limit: PAGE_SIZE,
        offset,
      }),
    placeholderData: keepPreviousData,
  });

  const { data: filterOptions } = useQuery({
    queryKey: ['anatel', 'paf', 'decision-filters'],
    queryFn: fetchPafDecisionFilters,
    staleTime: 5 * 60 * 1000,
  });

  // Corpus-wide and filter-independent, so fetched ONCE on its own key rather
  // than with every list request -- see the PADO page for the reasoning.
  const doctrine = useQuery({
    queryKey: ['anatel', 'paf', 'decisions', 'doctrine'],
    queryFn: fetchPafDecisionDoctrine,
    staleTime: Infinity,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'BRL' });

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <Link
          to="/anatel/paf"
          className="text-sm text-primary underline-offset-4 hover:underline"
        >
          {t('anatel:pafList.backToOverview')}
        </Link>
        <h1 className="text-lg font-semibold text-foreground">{t('anatel:pafList.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('anatel:pafList.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="anatel:pafList.explainer" />

      <FieldGuide i18nKey="anatel:pafList.guide" level={2} />

      {data?.as_of ? <AsOfStrip asOf={data.as_of} /> : null}

      <div className="flex justify-end">
        <ExportButton path={exportPath(query, relator, outcome, year, citation)} />
      </div>

      <PafFilterBar
        query={query}
        relator={relator}
        outcome={outcome}
        year={year}
        options={filterOptions}
        onChange={(patch) => {
          if (patch.query !== undefined) setQuery(patch.query);
          if (patch.relator !== undefined) setRelator(patch.relator);
          if (patch.outcome !== undefined) setOutcome(patch.outcome);
          if (patch.year !== undefined) setYear(patch.year);
          setOffset(0);
        }}
      />

      {citation ? (
        <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>
            {t('anatel:pafList.doctrine.filterActive', { citation })}
          </span>
          <button
            type="button"
            onClick={() => {
              setCitation('');
              setOffset(0);
            }}
            className="rounded border px-1.5 py-0.5 text-xs text-foreground hover:bg-muted"
          >
            {t('anatel:pafList.doctrine.filterClear')}
          </button>
        </p>
      ) : null}

      {doctrine.isPending ? (
        <SkeletonLoader rows={3} />
      ) : doctrine.error ? (
        <ErrorMessage message={doctrine.error.message} onRetry={() => void doctrine.refetch()} />
      ) : (
        <DoctrinePanel
          answer={doctrine.data.citation_lines}
          selected={citation || null}
          onSelectCitation={(next) => {
            setCitation(next);
            setOffset(0);
          }}
          exportPath="/api/v1/anatel/export/paf/citation-lines"
          i18nPrefix="pafList.doctrine"
          outcomeKeyPrefix="anatel:paf.outcomes"
        />
      )}

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : data.items.length === 0 ? (
        <EmptyState hint={t('anatel:pafList.emptyHint')} />
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground" aria-live="polite">
            {t('anatel:decisions.showing', {
              from: number.format(offset + 1),
              to: number.format(offset + data.items.length),
              total: number.format(data.total),
            })}
          </p>

          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('anatel:pafList.columns.case')}</th>
                  <th className="px-3 py-2 font-medium">{t('anatel:pafList.columns.date')}</th>
                  <th className="px-3 py-2 font-medium">{t('anatel:pafList.columns.entity')}</th>
                  <th className="px-3 py-2 font-medium">{t('anatel:pafList.columns.relator')}</th>
                  <th className="px-3 py-2 font-medium">{t('anatel:pafList.columns.outcome')}</th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('anatel:pafList.columns.credit')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.process_key} className="border-t hover:bg-muted/30">
                    <td className="px-3 py-2">
                      <Link
                        to={`/anatel/paf/c/${item.process_key}`}
                        className="font-medium text-primary underline-offset-4 hover:underline"
                      >
                        {item.process_number}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums text-muted-foreground">
                      {item.reference_date_raw ?? '—'}
                    </td>
                    {/* These fields are genuinely sparse -- entity on 81% of final
                        decisions, relator on 31% -- so an em dash is the honest
                        rendering, not a blank that reads as a layout bug. */}
                    <td className="max-w-xs truncate px-3 py-2 text-foreground">
                      {item.entity_name ?? '—'}
                    </td>
                    <td className="max-w-[12rem] truncate px-3 py-2 text-muted-foreground">
                      {item.relator ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {item.outcome_codes.length === 0
                        ? t('anatel:paf.outcomes.UNCLASSIFIED')
                        : item.outcome_codes
                            .map((code) => t(`anatel:paf.outcomes.${code}`, { defaultValue: code }))
                            .join(' · ')}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-foreground">
                      {/* Blank, never 0: a zero in a money column is a value. */}
                      {item.credit_amount_brl === null
                        ? ''
                        : currency.format(item.credit_amount_brl)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(offset - PAGE_SIZE, 0))}
              className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
            >
              {t('anatel:decisions.previous')}
            </button>
            <button
              type="button"
              disabled={offset + PAGE_SIZE >= data.total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
            >
              {t('anatel:decisions.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface PafFilterPatch {
  query?: string;
  relator?: string;
  outcome?: string;
  year?: string;
}

function PafFilterBar({
  query,
  relator,
  outcome,
  year,
  options,
  onChange,
}: {
  query: string;
  relator: string;
  outcome: string;
  year: string;
  options: PafDecisionFilters | undefined;
  onChange: (patch: PafFilterPatch) => void;
}) {
  const { t } = useTranslation(['anatel']);
  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="block max-w-md space-y-1">
        <span className="text-xs font-medium text-foreground">{t('anatel:pafList.search')}</span>
        <input
          type="search"
          value={query}
          placeholder={t('anatel:pafList.searchPlaceholder')}
          onChange={(event) => onChange({ query: event.target.value })}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
        />
      </label>

      <label className="block space-y-1">
        <span className="text-xs font-medium text-foreground">
          {t('anatel:pafList.filters.relator')}
        </span>
        <select
          value={relator}
          onChange={(event) => onChange({ relator: event.target.value })}
          className="rounded-md border bg-background px-3 py-2 text-sm text-foreground"
        >
          <option value="">{t('anatel:pafList.filters.allRelators')}</option>
          {(options?.relators ?? []).map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </label>

      <label className="block space-y-1">
        <span className="text-xs font-medium text-foreground">
          {t('anatel:pafList.filters.outcome')}
        </span>
        <select
          value={outcome}
          onChange={(event) => onChange({ outcome: event.target.value })}
          className="rounded-md border bg-background px-3 py-2 text-sm text-foreground"
        >
          <option value="">{t('anatel:pafList.filters.allOutcomes')}</option>
          {(options?.outcomes ?? []).map((code) => (
            <option key={code} value={code}>
              {t(`anatel:paf.outcomes.${code}`, { defaultValue: code })}
            </option>
          ))}
        </select>
      </label>

      <label className="block space-y-1">
        <span className="text-xs font-medium text-foreground">
          {t('anatel:pafList.filters.year')}
        </span>
        <select
          value={year}
          onChange={(event) => onChange({ year: event.target.value })}
          className="rounded-md border bg-background px-3 py-2 text-sm text-foreground"
        >
          <option value="">{t('anatel:pafList.filters.allYears')}</option>
          {(options?.years ?? []).map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function exportPath(
  query: string,
  relator: string,
  outcome: string,
  year: string,
  citation: string,
): string {
  const params = new URLSearchParams();
  if (query) params.set('query', query);
  if (relator) params.set('relator', relator);
  if (outcome) params.set('outcome', outcome);
  if (year) params.set('year', year);
  if (citation) params.set('citation', citation);
  const qs = params.toString();
  return `/api/v1/anatel/export/paf/decisions${qs ? `?${qs}` : ''}`;
}
